import { requestApi } from '@app/api';
import { withTemplate } from '@app/api/paths';
import { deleteTemplatesBulkily } from '@app/store/transport-layer/template/bulk/delete';
import Participant from '@app/store/transport-layer/template/Participant';
import RequesterInput from '@app/store/transport-layer/template/RequesterInput';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { Field as TransportLayerField } from '@app/store/transport-layer/types/field';
import { FieldGroup as TransportLayerFieldGroup } from '@app/store/transport-layer/types/fieldGroup';
import { Participant as TransportLayerParticipant } from '@app/store/transport-layer/types/participant';
import { WithId } from '@app/store/transport-layer/types/utility';
import { Verification as TransportLayerVerification } from '@app/store/transport-layer/types/verification';

// TODO: 차세대 에디터에서 템플릿 타입 정리 후 openapi 활용으로 수정하기

export interface TemplateDeleteResponse {
  requestCount: number;
  deletedCount: number;
}

/**
 * PUT 템플릿 리스트 삭제하기
 * @param {string[]} templateIds - 삭제할 템플릿의 ID 배열
 * @returns {Object} 삭제 결과
 *
 * @deprecated Endpoint is forwarded in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#ee6bd47ce4ab43c48c86e2c066cf309f
 */
export function deleteTemplates(templateIds: string[]): Promise<TemplateDeleteResponse> {
  return deleteTemplatesBulkily(templateIds);
}

export interface Template {
  attachments: { count: number };
  available: boolean;
  createdAt: string;
  file: { url: string } | null;
  id: string;
  originTemplateId: string | null;
  sharingCount: number;
  title: string;
  updatedAt: string;

  /* standardize-document-content 프로젝트 이전과 동일하게
     아래 요소들은 backend 인터페이스에 선언되어있지만 실제로 사용되지 않습니다. */
  participants: TransportLayerParticipant[];
}

export interface TemplatesResponse {
  count: number;
  templates: Template[];
}

export interface GetTemplateByIdResponse {
  createdAt: string;
  file: { url: string };
  id: string;
  originTemplateId: string | null;
  sharingCount: number;
  title: string;
  updatedAt: string;
  userId: string;
  attachments: TransportLayerAttachment['ToReceive'][];
  participants: TransportLayerParticipant[];
  fields: WithId<TransportLayerField['ToReceive']>[];
  fieldGroups: WithId<TransportLayerFieldGroup>[];
  verifications: TransportLayerVerification[];
  labelIds: string[];
}

/**
 * GET 템플릿 정보 가져오기
 * @params {string} templateId - 템플릿 id
 * @returns {object} template - 템플릿 정보
 * @property {object} file - 파일정보
 * @property {object[]} participants - 서명자 정보
 * @property {string} title - 템플릿 제목
 * @property {object[]} requesterInputs - 요청자 필드정보
 */
export function getTemplateById(workspaceId: string, templateId: string) {
  return requestApi<GetTemplateByIdResponse>({
    url: withTemplate(`/workspaces/${workspaceId}/templates/${templateId}`),
    method: 'GET',
    credentials: true,
  }).then((response) => {
    return response.data;
  });
}

interface PostTemplatesResponse {
  id: string;
  originTemplateId: string | null;
  participants: Participant[];
  requesterInputs: RequesterInput[];
  sharingCount: number;
  title: string;
  labelIds: string[];
}

/**
 * POST 템플릿 생성
 * Endpoint request is changed in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#b1c53bcab28a4269b210998035585706
 */
export function postTemplates(
  workspaceId: string,
  {
    attachments,
    fields,
    fieldGroups,
    labelIds,
    verifications,
    participants,
    keywords,
    file,
    title,
  }: {
    participants: TransportLayerParticipant[];
    fields: TransportLayerField['ToSend'][];
    fieldGroups: TransportLayerFieldGroup[];
    attachments: TransportLayerAttachment['ToSend'][];
    verifications: TransportLayerVerification[];
    labelIds: string[];
    keywords?: Array<string>;
    file: { id: string };
    title: string;
  }
): Promise<string> {
  return requestApi<PostTemplatesResponse>({
    url: withTemplate(`/workspaces/${workspaceId}/templates`),
    method: 'POST',
    data: {
      title,
      file,
      attachments,
      participants,
      verifications,
      labelIds,
      keywords,
      fields,
      fieldGroups,
    },
    credentials: true,
  }).then((response) => response.data.id);
}

/**
 * Endpoint request is changed in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#64445d8f7af1435985211775fa93e89a
 */
export function updateTemplate(
  workspaceId: string,
  templateId: string,
  {
    attachments,
    file,
    participants,
    fields,
    fieldGroups,
    title,
  }: {
    file: { id: string | null };
    participants: TransportLayerParticipant[];
    fields: TransportLayerField['ToSend'][];
    fieldGroups: TransportLayerFieldGroup[];
    attachments: TransportLayerAttachment['ToSend'][];
    title: string;
  }
): Promise<string> {
  return requestApi<PostTemplatesResponse>({
    url: withTemplate(`/workspaces/${workspaceId}/templates/${templateId}`),
    method: 'PUT',
    data: {
      file,
      attachments,
      participants,
      fields,
      fieldGroups,
      title,
    },
    credentials: true,
  }).then((response) => response.data.id);
}
